import React from "react";
import { useForm, Controller } from "react-hook-form";
import { Modal, Select, TextArea } from "@tyrannosaurustech/ui";
import { ParagraphS } from "../../../components/atoms/Typography";

interface BulkAddUsersModalProps {
  isOpen: boolean;
  onInvite: (emails: string[], roles: string) => void;
  onClose: () => void;
  isLoading: boolean;
  isFirstLoad?: boolean;
}

export const BulkAddUsersModal: React.FC<BulkAddUsersModalProps> = ({
  isOpen,
  onClose,
  onInvite,
  isLoading,
  isFirstLoad,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
  } = useForm<{
    emailAddresses: string;
    roles: string;
  }>({
    mode: "onChange",
  });

  const rolesOptions = [
    { id: "clientmanager", label: "Admin" },
    { id: "clientmember", label: "User" },
  ];

  const onSubmit = handleSubmit((data) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const emails = data.emailAddresses
      .split(/[\n,;]+/)
      .map((email: string) => email.trim())
      .filter(Boolean);

    const invalidEmails = emails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setError("emailAddresses", {
        type: "manual",
        message: `Invalid email addresses: ${invalidEmails.join(", ")}`,
      });
      return;
    }

    onInvite(emails, data.roles);
    reset();
  });

  return (
    <Modal
      onConfirm={onSubmit}
      open={isOpen}
      title="Add Users"
      setOpen={() => {
        reset();
        onClose();
      }}
      loading={isLoading}
      panelClassName="w-[600px]"
      confirmText="Invite"
      cancelText={isFirstLoad ? "Maybe Later" : "Cancel"}
      buttonAlignment="right"
      swapButtons={true}
      titleClassName="text-left font-bold text-xl font-semibold not-italic capitalize"
    >
      <div className="py-5">
        <ParagraphS>
          Enter the email addresses of the people you&apos;d like to add to the
          law firm.
        </ParagraphS>
        <div className="flex flex-col gap-4 mt-4">
          <Controller
            name="emailAddresses"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <label
                  htmlFor="emailAddresses"
                  className="text-st-sm flex items-center content-center dark:text-neutral-400 text-neutral-900"
                >
                  <p className="truncate">Email Addresses</p>
                </label>
                <TextArea
                  {...field}
                  placeholder="Enter or paste one or more email addresses"
                  rows={5}
                  className="w-full"
                />
                {errors.emailAddresses && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.emailAddresses.message}
                  </p>
                )}
              </>
            )}
          />
          <Controller
            name="roles"
            control={control}
            rules={{ required: true }}
            defaultValue={"clientmember"}
            render={({ field }) => (
              <Select
                {...field}
                label="Role"
                value={rolesOptions.find((option) => option.id === field.value)}
                onChange={(opt) => field.onChange(opt.id)}
                options={rolesOptions}
                className="w-full"
                placeholder=""
              />
            )}
          />
        </div>
      </div>
    </Modal>
  );
};
